import { Container, IInit } from '../../../common/container/Container'
import { ContentContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { prepareURL } from '../../../common/api/http-helpers'
import { catchError, map } from 'rxjs/operators'
import { Query } from '../../../common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { toModel, UserTreatmentDTO } from '../models/UserTreatmentDTO'
import { toModel as toModelWithTreatmentName } from '../models/UserTreatmentWithTreatmentNameDTO'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { UserTreatment, UserTreatmentQuery } from '../models/UserTreatment'
import { UserTreatmentWithTreatmentNameDTO } from '../models/UserTreatmentWithTreatmentNameDTO'
import { UserTreatmentWithTreatmentName } from '../models/UserTreatmentWithTreatmentName'

export interface IUserTreatmentApi extends IInit {
  getByID(id: string | null): Observable<UserTreatment | undefined>

  getFilteredList(q: Query<UserTreatmentQuery>): Observable<ItemList<UserTreatment>>

  getFilteredItems(q: Query<UserTreatmentQuery>): Observable<UserTreatment[]>

  getListWithTreatmentNameByUserID(
    q: Query<UserTreatmentQuery>
  ): Observable<ItemList<UserTreatmentWithTreatmentName>>

  add(e: UserTreatmentDTO): Observable<UserTreatment | undefined>

  update(e: UserTreatmentDTO): Observable<UserTreatment | undefined>

  delete(id: string): Observable<boolean>
}

export class UserTreatmentApi implements IUserTreatmentApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as ContentContainerConfig).moduleFullUrl + '/user_treatment'
  }

  getByID(id: string | null): Observable<UserTreatment | undefined> {
    return this._httpClient.get<UserTreatment>({ url: this._url + '/' + id }).pipe(
      map<UserTreatmentDTO, UserTreatment>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  add(e: UserTreatmentDTO): Observable<UserTreatment | undefined> {
    return this._httpClient.post<UserTreatment>({ url: this._url, body: e }).pipe(
      map<UserTreatmentDTO, UserTreatment>((d) => {
        this._statusService.sendStatus({ variant: 'success' })
        return toModel(d)
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getFilteredList(q: Query<UserTreatmentQuery>): Observable<ItemList<UserTreatment>> {
    return this._httpClient.get<ItemList<UserTreatment>>({ url: prepareURL(this._url, q) }).pipe(
      map<ItemList<UserTreatmentDTO>, ItemList<UserTreatment>>((dto) => {
        const itemList = emptyList<UserTreatment>()
        itemList.items = dto.items.map((d) => toModel(d))
        itemList.count = dto.count
        return itemList
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(emptyList<UserTreatment>())
      })
    )
  }

  getFilteredItems(q: Query<UserTreatmentQuery>): Observable<UserTreatment[]> {
    return this._httpClient.get<UserTreatment[]>({ url: prepareURL(this._url, q) }).pipe(
      map<UserTreatmentDTO[], UserTreatment[]>((dto) => dto.map((d) => toModel(d))),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of([])
      })
    )
  }

  getListWithTreatmentNameByUserID(
    q: Query<UserTreatmentQuery>
  ): Observable<ItemList<UserTreatmentWithTreatmentName>> {
    return this._httpClient
      .get<ItemList<UserTreatmentWithTreatmentNameDTO>>({
        url: prepareURL(this._url + '/user_treatment_with_treatment_name', q),
      })
      .pipe(
        map<ItemList<UserTreatmentWithTreatmentNameDTO>, ItemList<UserTreatmentWithTreatmentName>>(
          (dto) => {
            const itemList = emptyList<UserTreatmentWithTreatmentName>()
            itemList.items = dto.items.map((d) => toModelWithTreatmentName(d))
            itemList.count = dto.count
            return itemList
          }
        ),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<UserTreatmentWithTreatmentName>())
        })
      )
  }

  update(e: UserTreatmentDTO): Observable<UserTreatment | undefined> {
    return this._httpClient.put<UserTreatment>({ url: this._url, body: e }).pipe(
      map<UserTreatmentDTO, UserTreatment>((d) => {
        this._statusService.sendStatus({ variant: 'success' })
        return toModel(d)
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }
}
